import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import {
  PaymentDetails,
  PaymentDetailsProps,
} from './payment-details/payment-details';

interface PaymentDetailsRowProps {
  name: string;
  value: string;
}

export interface PaymentMethodProps {
  data?: PaymentDetailsProps;
  status: LoadingState;
}

export const PaymentMethod = types
  .model({
    data: types.optional(PaymentDetails, {}),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: PaymentMethodProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
    setData(props: Partial<PaymentDetailsProps>): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        data: { ...self.data, ...props },
      });
    },
  }))
  .actions((self) => ({
    async setStore(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.billings.paymentMethod,
      });
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setStore();
    },
    async update(): Promise<void> {
      await self.setStore();
    },
  }))
  .views((self) => ({
    getPaymentDetailsArray(): PaymentDetailsRowProps[] {
      const { data } = self;
      const snap = getSnapshot(data);
      return Object.entries(snap).map(([name, value]) => ({
        name,
        value,
      }));
    },
    getPaymentDetailsSnapshot(): PaymentDetailsProps {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const paymentMethod = PaymentMethod.create();
export type PaymentMethodSnapshot = SnapshotIn<typeof PaymentMethod>;
export type PaymentMethodInstance = Instance<typeof PaymentMethod>;
export default paymentMethod;
